import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';

import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import { pipingRegEx } from 'components/FormattedInput/helpers';

import { Matcher, Operator } from 'utils/adl-services';

import {
  getEmptyRow,
  getEmptyColumn,
} from 'containers/QuestionnairePage/GenericQuestion/MatrixQuestion/utils';
import {
  getNoneOfTheAboveEmptyAnswer,
  isCarriedForward,
  noneOfTheAboveAnswerSelector,
} from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';

import { cloneDeep, find, get, last } from 'lodash';
import {
  mapQuestionsAndGroupsAsNodes,
  duplicateQuestion as duplicateQuestionHelper,
} from 'containers/QuestionnairePage/utils';
import { ANSWER_DEFAULT } from 'containers/QuestionnairePage/constants';
import { SURVEY_TYPE_PEEQ } from 'containers/Survey/constants';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';

const brandDiagnosticGenerator = ({ template, variables, surveyType }) => {
  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const yourBrand = get(find(variables, ['name', 'your_brand']), 'value', {});
  const cycle = get(find(variables, ['name', 'cycle']), 'value', '');
  const competitors = get(
    find(variables, ['name', 'competitors']),
    'value',
    [],
  );
  const perceptions = get(
    find(variables, ['name', 'perceptions']),
    'value',
    [],
  );
  const fallout = get(find(variables, ['name', 'fallout']), 'value', []);
  const attributes = get(find(variables, ['name', 'attributes']), 'value', []);
  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  const replaceProductCategory = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );

  const replaceCycle = (value) =>
    value.replace(/\{\{cycle\}\}/gi, escapeHTML(cycle));

  const templateWithProductCategory = transformPropsDeep(
    template.questionnaireTemplate,
    {
      prompt: replaceProductCategory,
      htmlPrompt: replaceProductCategory,
      body: replaceCycle,
    },
  );

  const { nodes: brandDiagnosticTemplateQuestions } =
    mapQuestionsAndGroupsAsNodes(templateWithProductCategory);

  //
  // 1 product_category and cycle
  // 2 product_category
  // 3 product_category
  // 4 product_category and answers (your_brand & competitors & NOTA)
  // 5 answers (your_brand & competitors)
  // 6 answers (your_brand & competitors)
  // 7 product_category and answers (your_brand & competitors & NOTA)
  // 8 CF from Q7 (selected)
  // 9 CF from Q7 (unselected) and scorepoints (fallout)
  // 10 product_category and answers (your_brand & competitors & NOTA)
  // 11 CF from Q10 (selected) and OTHER and NOTA
  // 12 CF from Q10 (selected) + specific answer + NOTA (custom body)
  // 13 recall from Q11
  // 14 GROUP (repeat for your_brand and each competitor)
  // 14 brand/competitor + media
  // 15 brand/competitor + media and answers (perceptions + NOTA)
  // 20 - (affected by group 14 repetitions) - product category and answers (attributes)
  // 21 (repeat for your_brand and each competirtor) - answers (attributes)

  const brandAndCompetitorAnswers = [
    // your_brand
    Object.assign(getEmptyRow({ isEmptyAnswer: false }), {
      body: yourBrand.text,
      media: yourBrand.media,
    }),
    // competitors
    ...competitors.map((competitor) =>
      Object.assign(getEmptyRow({ isEmptyAnswer: false }), {
        body: competitor.text,
        media: competitor.media,
      }),
    ),
  ];

  const questionsWithProductCategoryMedia = [0, 1, 2, 3, 6, 9, 16];
  questionsWithProductCategoryMedia.forEach((questionIdex) => {
    Object.assign(brandDiagnosticTemplateQuestions[questionIdex], {
      media: productCategory.media,
    });
  });

  const initialQuestions = brandDiagnosticTemplateQuestions.slice(0, 13);
  const brandOpinionGroup = brandDiagnosticTemplateQuestions[13];
  const productCategoryAttributesQuestion =
    brandDiagnosticTemplateQuestions[16];
  const brandRatingQuestion = brandDiagnosticTemplateQuestions[17];

  // add dynamic answers where required
  Object.assign(initialQuestions[3], {
    answers: [
      ...brandAndCompetitorAnswers,
      getNoneOfTheAboveEmptyAnswer({ body: 'None of the above', alias: 'NON' }),
    ],
  });
  Object.assign(initialQuestions[4], { answers: brandAndCompetitorAnswers });
  Object.assign(initialQuestions[5], { answers: brandAndCompetitorAnswers });
  Object.assign(initialQuestions[6], {
    answers: [
      ...brandAndCompetitorAnswers,
      getNoneOfTheAboveEmptyAnswer({ body: 'None of the above', alias: 'NON' }),
    ],
  });
  Object.assign(initialQuestions[8], {
    scorepoints: fallout.map((falloutText) => ({
      ...getEmptyColumn({ isEmptyAnswer: false }),
      body: falloutText,
    })),
  });
  Object.assign(initialQuestions[9], {
    answers: [
      ...brandAndCompetitorAnswers,
      getNoneOfTheAboveEmptyAnswer({ body: 'None of the above', alias: 'NON' }),
    ],
  });

  Object.assign(brandOpinionGroup.questions[1], {
    answers: [
      ...perceptions.map((perceptionText) => ({
        ...getEmptyRow({ isEmptyAnswer: false }),
        body: perceptionText,
      })),
      getNoneOfTheAboveEmptyAnswer({ body: 'None of the above', alias: 'NON' }),
    ],
  });

  Object.assign(productCategoryAttributesQuestion, {
    answers: attributes.map((attributeText) => ({
      ...getEmptyRow({ isEmptyAnswer: false }),
      body: attributeText,
    })),
    media: productCategory.media,
  });

  Object.assign(brandRatingQuestion, {
    answers: attributes.map((attributeText) => ({
      ...getEmptyRow({ isEmptyAnswer: false }),
      body: attributeText,
    })),
  });

  const initialQuestionDuplicates = initialQuestions.map(
    duplicateQuestionHelper,
  );

  // fix CF links and Recalls
  Object.assign(initialQuestionDuplicates[7], {
    answers: initialQuestionDuplicates[7].answers.filter(
      (answer) => !isCarriedForward(answer),
    ),
  });
  initialQuestionDuplicates[7].carryForward[0].questionRefId =
    initialQuestionDuplicates[6].refId;

  Object.assign(initialQuestionDuplicates[8], {
    answers: initialQuestionDuplicates[8].answers.filter(
      (answer) => !isCarriedForward(answer),
    ),
  });
  initialQuestionDuplicates[8].carryForward[0].questionRefId =
    initialQuestionDuplicates[6].refId;

  Object.assign(initialQuestionDuplicates[10], {
    answers: initialQuestionDuplicates[10].answers.filter(
      (answer) => !isCarriedForward(answer),
    ),
  });
  initialQuestionDuplicates[10].carryForward[0].questionRefId =
    initialQuestionDuplicates[9].refId;

  Object.assign(initialQuestionDuplicates[11], {
    answers: initialQuestionDuplicates[11].answers.filter(
      (answer) => !isCarriedForward(answer),
    ),
  });
  initialQuestionDuplicates[11].carryForward[0].questionRefId =
    initialQuestionDuplicates[9].refId;

  const replaceRecall = (value) =>
    value.replace(
      pipingRegEx,
      `{{refId:${initialQuestionDuplicates[10].refId}}}`,
    );

  initialQuestionDuplicates[12] = transformPropsDeep(
    initialQuestionDuplicates[12],
    {
      prompt: replaceRecall,
      htmlPrompt: replaceRecall,
    },
  );

  const Survey = new TemplateSurveyState({
    // fetchedQuestionnaire: template.questionnaireTemplate,
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  initialQuestionDuplicates.forEach((question) => {
    Survey.questionnaire.addQuestion(question);
  });

  const opinionGroups = [yourBrand, ...competitors].map((brandOrCompetitor) => {
    const groupClone = cloneDeep(brandOpinionGroup);

    const replaceBrandText = (value) =>
      value.replace(/\{\{your_brand\}\}/gi, escapeHTML(brandOrCompetitor.text));

    const brandGroupWithTexts = transformPropsDeep(groupClone, {
      prompt: replaceBrandText,
      htmlPrompt: replaceBrandText,
    });
    brandGroupWithTexts.questions.forEach((question) => {
      Object.assign(question, { media: brandOrCompetitor.media });
    });

    const brandGroup = duplicateQuestionHelper(brandGroupWithTexts);
    return brandGroup;
  });

  opinionGroups.forEach((group) => {
    Survey.questionnaire.addQuestion(group);
  });

  const duplicateProductCategoryAttributesQuestion = duplicateQuestionHelper(
    productCategoryAttributesQuestion,
  );
  Survey.questionnaire.addQuestion(duplicateProductCategoryAttributesQuestion);

  const duplicateBrandRatingQuestions = [yourBrand, ...competitors].map(
    (brandOrCompetitor) => {
      const brandRatingClone = cloneDeep(brandRatingQuestion);

      const replaceBrandText = (value) =>
        value.replace(
          /\{\{your_brand\}\}/gi,
          escapeHTML(brandOrCompetitor.text),
        );
      const brandRatingWithTexts = transformPropsDeep(brandRatingClone, {
        prompt: replaceBrandText,
        htmlPrompt: replaceBrandText,
      });
      Object.assign(brandRatingWithTexts, { media: brandOrCompetitor.media });

      const brandRating = duplicateQuestionHelper(brandRatingWithTexts);
      return brandRating;
    },
  );
  duplicateBrandRatingQuestions.forEach((question) => {
    Survey.questionnaire.addQuestion(question);
  });
  // second pass for CF
  Survey.questionnaire.updateQuestion(initialQuestionDuplicates[6]);
  Survey.questionnaire.updateQuestion(initialQuestionDuplicates[9]);

  // LOGIC
  Survey.questionnaire.linkAnswerToQuestion({
    question: initialQuestionDuplicates[3],
    answerRefId: noneOfTheAboveAnswerSelector(
      initialQuestionDuplicates[3].answers,
    ).refId,
    targetQuestion: initialQuestionDuplicates[11],
  });

  Survey.questionnaire.linkAnswerToQuestion({
    question: initialQuestionDuplicates[6],
    answerRefId: noneOfTheAboveAnswerSelector(
      initialQuestionDuplicates[6].answers,
    ).refId,
    targetQuestion: initialQuestionDuplicates[8],
  });

  initialQuestionDuplicates[6].answers
    .filter(({ type }) => type === ANSWER_DEFAULT)
    .forEach((answer, answerIndex) => {
      const questionToAttach = initialQuestionDuplicates[7];
      const questionToGoTo = initialQuestionDuplicates[9];
      const context = { questionId: initialQuestionDuplicates[6].refId };
      if (answerIndex === 0) {
        Survey.questionnaire.linkAnswerToQuestion({
          question: questionToAttach,
          answerRefId: answer.refId,
          targetQuestion: questionToGoTo,
          context,
        });
      } else {
        Survey.questionnaire.addRule(questionToAttach, answer, context);
      }
    });
  Survey.questionnaire.updateRuleOperator(
    initialQuestionDuplicates[7],
    Operator.All,
  );

  Survey.questionnaire.linkAnswerToQuestion({
    question: initialQuestionDuplicates[9],
    answerRefId: noneOfTheAboveAnswerSelector(
      initialQuestionDuplicates[9].answers,
    ).refId,
    targetQuestion: initialQuestionDuplicates[11],
  });

  initialQuestionDuplicates[11].answers
    .filter((answer) => {
      // select non-carried forward
      if (!isCarriedForward(answer)) {
        return true;
      }
      // find original CF answer
      const originAnswer = noneOfTheAboveAnswerSelector(
        initialQuestionDuplicates[9].answers,
      );
      // and select the NoneOfTheAbove CF answer
      if (originAnswer.refId === answer.originalAnswerRefId) {
        return true;
      }
      return false;
    })
    .forEach((answer) => {
      const questionToAttach = initialQuestionDuplicates[11];
      const questionToGoTo = duplicateProductCategoryAttributesQuestion;

      Survey.questionnaire.linkAnswerToQuestion({
        question: questionToAttach,
        answerRefId: answer.refId,
        targetQuestion: questionToGoTo,
      });
    });

  [
    duplicateProductCategoryAttributesQuestion,
    ...duplicateBrandRatingQuestions,
  ].forEach((question, questionIndex, allQuestions) => {
    const sourceQuestion = initialQuestionDuplicates[5];
    const context = { questionId: sourceQuestion.refId };
    const column = last(sourceQuestion.scorepoints).refId;

    sourceQuestion.answers
      .slice(questionIndex)
      .forEach(({ refId: row }, rowIndex) => {
        Survey.questionnaire.linkAnswerToQuestion({
          question,
          answerRefId: { row, column },
          targetQuestion: allQuestions[questionIndex + 1 + rowIndex],
          context,
          matcher: Matcher.NotEqual,
        });
      });

    Survey.questionnaire.setQuestionDefaultOutcome(question, {
      refId: END_OF_SURVEY,
    });
  });

  return { state: Survey.state, defaultRecurrence };
};

export default brandDiagnosticGenerator;
